'use client';

import { useEffect, useRef } from 'react';

export default function CtaSection() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('revealed');
        });
      },
      { threshold: 0.1 }
    );
    const elements = sectionRef?.current?.querySelectorAll('.reveal-hidden');
    elements?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  return (
    <section ref={sectionRef} id="cta" className="py-28 px-6 bg-cream relative overflow-hidden">
      {/* Decorative circles */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full border border-sage/8 pointer-events-none" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] rounded-full border border-sage/12 pointer-events-none" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[200px] h-[200px] rounded-full border border-sage/20 pointer-events-none" />

      {/* Ambient background */}
      <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-sage/5 rounded-full blur-[80px] pointer-events-none" />
      <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-sand/10 rounded-full blur-[80px] pointer-events-none" />

      <div className="max-w-3xl mx-auto text-center relative z-10">
        {/* Badge */}
        <div className="reveal-hidden inline-flex items-center gap-2 badge-sage rounded-full px-4 py-1.5 text-xs font-semibold uppercase tracking-widest mb-8">
          <span className="w-1.5 h-1.5 rounded-full bg-sage inline-block animate-pulse" />
          Jetzt starten
        </div>

        {/* Headline */}
        <h2 className="reveal-hidden font-serif text-4xl md:text-6xl font-light text-charcoal leading-[1.05] mb-6 tracking-tight">
          Bereit für dein{' '}
          <span className="italic text-sage">nächstes Kapitel?</span>
        </h2>

        <p className="reveal-hidden delay-100 text-muted text-lg md:text-xl font-light leading-relaxed mb-10 max-w-2xl mx-auto">
          Ein kostenloses Erstgespräch. Keine Verpflichtung.
          Nur du, dein Ikigai – und der erste Schritt in eine Richtung, die sich richtig anfühlt.
        </p>

        {/* CTA Buttons */}
        <div className="reveal-hidden delay-200 flex flex-col sm:flex-row gap-4 justify-center items-center mb-4">
          <a
            href="https://calendly.com/azav_consulting/ikigai-karriere-check"
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary text-base font-semibold px-10 py-4 rounded-full inline-block"
          >
            Kostenloses Erstgespräch buchen
          </a>
        </div>

        {/* Zoom meeting note */}
        <p className="reveal-hidden delay-200 text-sm text-muted/70 italic mb-8">
          Das Gespräch findet via Zoom statt
        </p>

        {/* Trust signals */}
        <div className="reveal-hidden delay-300 flex flex-col sm:flex-row items-center justify-center gap-6 text-sm text-muted">
          <div className="flex items-center gap-2">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6B8F6E" strokeWidth="2">
              <path d="M20 6L9 17l-5-5" />
            </svg>
            <span>100% kostenfrei (AVGS)</span>
          </div>
          <div className="w-1 h-1 rounded-full bg-sand hidden sm:block" />
          <div className="flex items-center gap-2">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6B8F6E" strokeWidth="2">
              <path d="M20 6L9 17l-5-5" />
            </svg>
            <span>Keine Verpflichtung</span>
          </div>
          <div className="w-1 h-1 rounded-full bg-sand hidden sm:block" />
          <div className="flex items-center gap-2">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#6B8F6E" strokeWidth="2">
              <path d="M20 6L9 17l-5-5" />
            </svg>
            <span>Staatlich anerkannte Maßnahme</span>
          </div>
        </div>
      </div>
    </section>
  );
}